import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import PermissionGate from '../components/PermissionGate';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import {
  Search,
  Save,
  X,
  Edit,
  Key,
  Crown,
  Users,
  Package,
  CreditCard,
  ShoppingCart,
  BarChart3,
  Settings
} from 'lucide-react';

const RolePermissionManagement = () => {
  const { user } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  const [loading, setLoading] = useState(false);

  // Data State
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showRolePermissionsModal, setShowRolePermissionsModal] = useState(false);


  // Load data on component mount
  useEffect(() => {
    fetchPermissions();
    fetchRoles();
    fetchPermissionGroups();
  }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await apiService.request('/permissions');
      if (response.success) {
        setPermissions(response.data);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apiService.request('/roles');
      if (response.success) {
        setRoles(response.data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to fetch roles');
    }
  };

  const fetchPermissionGroups = async () => {
    try {
      const response = await apiService.request('/permissions/groups');
      if (response.success) {
        setPermissionGroups(response.data);
      }
    } catch (error) {
      console.error('Error fetching permission groups:', error);
      toast.error('Failed to fetch permission groups');
    }
  };

  const handleRolePermissions = (role) => {
    setSelectedRole(role);
    setShowRolePermissionsModal(true);
  };

  const handleUpdateRolePermissions = async (roleId, selectedPermissions) => {
    try {
      const response = await apiService.request(`/roles/${roleId}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permissions: selectedPermissions })
      });

      if (response.success) {
        toast.success('Role permissions updated successfully!');
        setShowRolePermissionsModal(false);
        setSelectedRole(null);
        fetchRoles(); // Refresh roles data
      }
    } catch (error) {
      console.error('Error updating role permissions:', error);
      toast.error('Failed to update role permissions');
    }
  };

  const getRoleColor = (roleSlug) => {
    switch (roleSlug) {
      case 'super_admin': return 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 border border-orange-200';
      case 'admin': return 'bg-red-100 text-red-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'cashier': return 'bg-green-100 text-green-800';
      case 'viewer': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (roleSlug) => {
    switch (roleSlug) {
      case 'super_admin': return '⭐';
      case 'admin': return '👑';
      case 'manager': return '💼';
      case 'cashier': return '🛒';
      case 'viewer': return '👁️';
      default: return '👤';
    }
  };

  const getGroupIcon = (group) => {
    switch (group) {
      case 'users': return <Users className="w-4 h-4" />;
      case 'products': return <Package className="w-4 h-4" />;
      case 'pos': return <CreditCard className="w-4 h-4" />;
      case 'sales': return <ShoppingCart className="w-4 h-4" />;
      case 'inventory': return <Package className="w-4 h-4" />;
      case 'reports': return <BarChart3 className="w-4 h-4" />;
      case 'cash': return <CreditCard className="w-4 h-4" />;
      case 'settings': return <Settings className="w-4 h-4" />;
      default: return <Key className="w-4 h-4" />;
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
            {/* Page Header */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-between mt-16">
                <div>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-900 bg-clip-text text-transparent">
                    Roles & Permissions
                  </h1>
                  <p className="text-gray-600 text-sm sm:text-base mt-2">Manage system roles and their permissions</p>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      fetchPermissions();
                      fetchRoles();
                      fetchPermissionGroups();
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Refresh
                  </button>
                </div>
              </div>
            </div>

          {/* Roles Section */}
          <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-6 shadow-lg border border-blue-100/50 backdrop-blur-sm mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Crown className="w-5 h-5 mr-2 text-yellow-500" />
              System Roles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roles.map((role) => (
                <div key={role.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{getRoleIcon(role.slug)}</span>
                      <h3 className="font-semibold text-gray-900">{role.name}</h3>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(role.slug)}`}>
                      {role.permissions.length} permissions
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{role.description}</p>
                  <button
                    onClick={() => handleRolePermissions(role)}
                    className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Manage Permissions</span>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Permissions Section */}
          <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-6 shadow-lg border border-blue-100/50 backdrop-blur-sm">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Key className="w-5 h-5 mr-2 text-blue-500" />
              System Permissions
            </h2>
            <div className="space-y-6">
              {permissions.map((group) => (
                <div key={group.group} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                  <div className="flex items-center space-x-2 mb-3">
                    {getGroupIcon(group.group)}
                    <h3 className="font-semibold text-gray-900 capitalize">{group.group}</h3>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                      {group.permissions.length} permissions
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {group.permissions.map((permission) => (
                      <div key={permission.id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-gray-700">{permission.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
      </div>

      {/* Role Permissions Modal */}
      {showRolePermissionsModal && selectedRole && (
        <RolePermissionsModal
          role={selectedRole}
          permissions={permissions}
          onClose={() => {
            setShowRolePermissionsModal(false);
            setSelectedRole(null);
          }}
          onSave={handleUpdateRolePermissions}
        />
      )}
    </div>
    </Layout>
  );
};

// Role Permissions Modal Component
const RolePermissionsModal = ({ role, permissions, onClose, onSave }) => {
  const [selectedPermissions, setSelectedPermissions] = useState(
    role.permissions.map(p => p.id)
  );

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleGroupToggle = (groupPermissions) => {
    const groupPermissionIds = groupPermissions.map(p => p.id);
    const allSelected = groupPermissionIds.every(id => selectedPermissions.includes(id));
    
    if (allSelected) {
      // Deselect all permissions in this group
      setSelectedPermissions(prev => prev.filter(id => !groupPermissionIds.includes(id)));
    } else {
      // Select all permissions in this group
      setSelectedPermissions(prev => [...new Set([...prev, ...groupPermissionIds])]);
    }
  };

  const handleSave = () => {
    onSave(role.id, selectedPermissions);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 flex items-center">
            <span className="mr-2">{role.slug === 'super_admin' ? '⭐' : role.slug === 'admin' ? '👑' : role.slug === 'manager' ? '💼' : role.slug === 'cashier' ? '🛒' : '👁️'}</span>
            Manage Permissions for {role.name}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {permissions.map((group) => {
            const groupPermissionIds = group.permissions.map(p => p.id);
            const allSelected = groupPermissionIds.every(id => selectedPermissions.includes(id));
            const someSelected = groupPermissionIds.some(id => selectedPermissions.includes(id));

            return (
              <div key={group.group} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={input => {
                        if (input) input.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={() => handleGroupToggle(group.permissions)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <h4 className="font-semibold text-gray-900 capitalize">{group.group}</h4>
                    <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded-full text-xs font-medium">
                      {group.permissions.length} permissions
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 ml-6">
                  {group.permissions.map((permission) => (
                    <label key={permission.id} className="flex items-center space-x-2 p-2 bg-white rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(permission.id)}
                        onChange={() => handlePermissionToggle(permission.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{permission.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>Save Permissions</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RolePermissionManagement;
